import DynamicLogo from '../../common/img/DynamicLogo';
import clsx from 'clsx';

interface CommonPoolIconProps {
  logos: string[];
  /** Single symbol, or one symbol per logo so each missing logo falls back to its own letter */
  symbols: string | string[];
  chainLogo: string;
  chainName: string;
  maxTokens?: number;
  chainIconStyle?: string;
  containerStyle?: string;
  logoStyle?: string;
  showChainIcon?: boolean;
  chainIconDivStyle?: string;
  /** Border classes for logos that load, so overlapping icons stay separated (none by default) */
  logoBorderStyle?: string;
  fallBackStyle?: string;
}

const CommonPoolIcon: React.FC<CommonPoolIconProps> = ({
  logos,
  symbols,
  chainLogo,
  chainName,
  maxTokens = 5,
  chainIconStyle,
  containerStyle,
  logoStyle,
  showChainIcon = true,
  chainIconDivStyle,
  logoBorderStyle,
  fallBackStyle = 'bg-tertiary! border!',
}) => {
  const normalizedMaxTokens = Math.max(1, maxTokens);
  const hasMoreTokens = logos.length > normalizedMaxTokens;
  const visibleLogos = hasMoreTokens ? logos.slice(0, Math.max(1, normalizedMaxTokens - 1)) : logos;
  const remainingTokensCount = logos.length - visibleLogos.length;
  // With a border on the wrapper, sizing the img to logoStyle overflows the content box; fill it instead
  const logoImgStyle = logoBorderStyle ? 'w-full! h-full!' : clsx('w-5! h-5!', logoStyle);
  const fallbackSymbol = (index: number) => (Array.isArray(symbols) ? (symbols[index] ?? '') : symbols);

  return (
    <div
      className={clsx('flex relative -space-x-2 sm:-space-x-1.5 items-center justify-center shrink-0', containerStyle)}
    >
      {visibleLogos.map((logoUri, i) => (
        <DynamicLogo
          key={`${logoUri}_${i}`}
          imgStyle={logoImgStyle}
          logo={logoUri}
          fallBackCharacter={fallbackSymbol(i)}
          labelStyle="text-xs!"
          className={clsx(
            'shrink-0',
            logoBorderStyle ?? (logoUri ? 'border-none' : 'border border-secondary rounded-full'),
            !logoUri && 'w-5! h-5!',
            logoStyle,
          )}
          fallBackStyle={fallBackStyle}
        />
      ))}
      {hasMoreTokens && (
        <div
          className={clsx(
            'w-5 h-5 rounded-full text-xxs font-medium flex items-center justify-center shrink-0',
            logoBorderStyle ?? 'border border-secondary',
            fallBackStyle,
            logoStyle,
          )}
        >
          +{remainingTokensCount}
        </div>
      )}
      {showChainIcon && (
        <div className={clsx('absolute -right-2.5 top-0', chainIconDivStyle)}>
          <DynamicLogo imgStyle={clsx('w-3 h-3', chainIconStyle)} logo={chainLogo} fallBackCharacter={chainName} />
        </div>
      )}
    </div>
  );
};

export default CommonPoolIcon;
